import axios from 'axios';
import React, { useState, useEffect } from 'react';
import Pagination from '../../components/Pagination.tsx';
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from '../../theme.ts';

interface Transaction {
    id: string;
    balance: number;
    type: "CREDIT" | "DEBIT";
    type_display: string;
    reason: string;
    created_at: string;
}

const AdminWallets = () => {
    const { themeName } = useTheme();
    const currentTheme = getTheme(themeName);
    const rawData = localStorage.getItem('user');
    const cred = rawData ? JSON.parse(rawData) : null;
    const token = cred?.access;
    const [walletTransactions, setWalletTransactions] = useState<Transaction[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const getAllWalletTransactions = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${process.env.REACT_APP_DB_URI}/wallet-transaction/`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            
            // Handle different response structures
            let transactions: Transaction[] = [];
            
            if (Array.isArray(response.data)) {
                // If response is directly an array
                transactions = response.data;
            } else if (response.data?.results && Array.isArray(response.data.results)) {
                // If response is paginated with results
                transactions = response.data.results;
            } else if (response.data?.data && Array.isArray(response.data.data)) {
                // If response has a data property
                transactions = response.data.data;
            } else if (response.data && typeof response.data === 'object') {
                // If it's a single object, convert to array
                transactions = [response.data];
            }
            
            setWalletTransactions(transactions);
        } catch (err) {
            console.error('Error fetching transactions:', err);
            setError('Failed to load transactions');
            setWalletTransactions([]); // Set empty array on error
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getAllWalletTransactions();
    }, []);

    // Safe pagination calculations - check if walletTransactions is array
    const safeTransactions = Array.isArray(walletTransactions) ? walletTransactions : [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedTransactions = safeTransactions.slice(startIndex, endIndex);
    const totalPages = Math.ceil(safeTransactions.length / rowsPerPage);

    // Format amount with currency symbol
    const formatAmount = (amount: number, type: string) => {
        const formattedAmount = new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
        
        return type === "CREDIT" ? `+${formattedAmount}` : `-${formattedAmount}`;
    };

    // Format date
    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Invalid date';
        }
    };

    // Get transaction icon
    const getTransactionIcon = (type: string) => {
        return type === "CREDIT" ? "📥" : "📤";
    };

    if (isLoading) {
        return (
            <div className="min-h-screen p-4 md:p-6 lg:p-8" style={{ backgroundColor: currentTheme.background }}>
                <div className="flex flex-col items-center justify-center h-64">
                    <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mb-4"
                         style={{ borderColor: `${currentTheme.primary}40`, borderTopColor: currentTheme.primary }} />
                    <p style={{ color: currentTheme.text }}>Loading transactions...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen p-4 md:p-6 lg:p-8" style={{ backgroundColor: currentTheme.background }}>
                <div className="text-center py-12">
                    <div className="text-6xl mb-4">😕</div>
                    <h2 className="text-2xl font-bold mb-2" style={{ color: currentTheme.error }}>
                        Error Loading Transactions
                    </h2>
                    <p className="text-sm mb-4" style={{ color: currentTheme.text }}>{error}</p>
                    <button
                        onClick={getAllWalletTransactions}
                        className="px-4 py-2 rounded-lg transition-colors"
                        style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4 md:p-6 lg:p-8" style={{ backgroundColor: currentTheme.background }}>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: currentTheme.primary }}>
                        💰 Wallet Transactions
                    </h1>
                    <p className="text-sm opacity-70" style={{ color: currentTheme.text }}>
                        Monitor all wallet transactions across the platform
                    </p>
                </div>

                {/* Transactions Table */}
                <div className="shadow-lg rounded-xl overflow-hidden border" style={{ 
                    backgroundColor: currentTheme.surface,
                    borderColor: currentTheme.border 
                }}>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 z-10" style={{ backgroundColor: currentTheme.primary }}>
                                <tr>
                                    <th className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.inverseText }}>
                                        Transaction ID
                                    </th>
                                    <th className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.inverseText }}>
                                        Amount
                                    </th>
                                    <th className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.inverseText }}>
                                        Type
                                    </th>
                                    <th className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.inverseText }}>
                                        Reason
                                    </th>
                                    <th className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: currentTheme.inverseText }}>
                                        Date & Time
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y" style={{ borderColor: currentTheme.border }}>
                                {paginatedTransactions.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="text-center py-12">
                                            <div className="text-4xl mb-3">📭</div>
                                            <p className="text-base" style={{ color: currentTheme.textSecondary }}>
                                                No transactions found
                                            </p>
                                            <p className="text-sm mt-1 opacity-60" style={{ color: currentTheme.text }}>
                                                Transactions will appear here when users make wallet transactions
                                            </p>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedTransactions.map((txn) => (
                                        <tr 
                                            key={txn.id} 
                                            className="transition-all hover:bg-opacity-50"
                                            style={{ 
                                                backgroundColor: currentTheme.surface,
                                                borderBottom: `1px solid ${currentTheme.border}` 
                                            }}
                                        >
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-lg">{getTransactionIcon(txn.type)}</span>
                                                    <span className="font-mono text-xs" style={{ color: currentTheme.textSecondary }}>
                                                        {txn.id.slice(0, 8)}...
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4">
                                                <span className="font-semibold" style={{ 
                                                    color: txn.type === "CREDIT" ? currentTheme.success : currentTheme.error 
                                                }}>
                                                    {formatAmount(txn.balance, txn.type)}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4">
                                                <span 
                                                    className="px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center"
                                                    style={{
                                                        backgroundColor: txn.type === "CREDIT" ? `${currentTheme.success}20` : `${currentTheme.error}20`,
                                                        color: txn.type === "CREDIT" ? currentTheme.success : currentTheme.error,
                                                        border: `1px solid ${txn.type === "CREDIT" ? currentTheme.success + '40' : currentTheme.error + '40'}`
                                                    }}
                                                >
                                                    {txn.type_display || txn.type}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4" style={{ color: currentTheme.text }}>
                                                {txn.reason || '-'}
                                            </td>
                                            <td className="py-3 px-4 text-sm" style={{ color: currentTheme.textSecondary }}>
                                                <div className="flex items-center gap-1">
                                                    <span>🕒</span>
                                                    {formatDate(txn.created_at)}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {safeTransactions.length > 0 && (
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t" 
                             style={{ borderColor: currentTheme.border }}>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm" style={{ color: currentTheme.textSecondary }}>
                                        Show:
                                    </span>
                                    <select
                                        value={rowsPerPage}
                                        onChange={(e) => {
                                            setRowsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                        className="px-3 py-1.5 rounded-lg text-sm border focus:outline-none focus:ring-2 transition-shadow"
                                        style={{
                                            backgroundColor: currentTheme.surface,
                                            color: currentTheme.text,
                                            borderColor: currentTheme.border,
                                        }}
                                    >
                                        <option value={10}>10 per page</option>
                                        <option value={25}>25 per page</option>
                                        <option value={50}>50 per page</option>
                                        <option value={100}>100 per page</option>
                                    </select>
                                </div>
                                
                                <span className="text-sm px-3 py-1.5 rounded-lg" 
                                      style={{ backgroundColor: `${currentTheme.primary}10`, color: currentTheme.primary }}>
                                    {startIndex + 1}-{Math.min(endIndex, safeTransactions.length)} of {safeTransactions.length}
                                </span>
                            </div>

                            <Pagination 
                                currentPage={currentPage} 
                                totalPages={totalPages} 
                                onPageChange={setCurrentPage} 
                            />
                        </div>
                    )}
                </div>

                {/* Summary Stats */}
                {safeTransactions.length > 0 && (
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 rounded-lg border" style={{ 
                            backgroundColor: currentTheme.surface,
                            borderColor: currentTheme.border 
                        }}>
                            <div className="text-sm opacity-60 mb-1" style={{ color: currentTheme.text }}>Total Transactions</div>
                            <div className="text-2xl font-bold" style={{ color: currentTheme.primary }}>
                                {safeTransactions.length}
                            </div>
                        </div>
                        <div className="p-4 rounded-lg border" style={{ 
                            backgroundColor: currentTheme.surface,
                            borderColor: currentTheme.border 
                        }}>
                            <div className="text-sm opacity-60 mb-1" style={{ color: currentTheme.text }}>Total Credits</div>
                            <div className="text-2xl font-bold" style={{ color: currentTheme.success }}>
                                {safeTransactions.filter(t => t.type === "CREDIT").length}
                            </div>
                        </div>
                        <div className="p-4 rounded-lg border" style={{ 
                            backgroundColor: currentTheme.surface,
                            borderColor: currentTheme.border 
                        }}>
                            <div className="text-sm opacity-60 mb-1" style={{ color: currentTheme.text }}>Total Debits</div>
                            <div className="text-2xl font-bold" style={{ color: currentTheme.error }}>
                                {safeTransactions.filter(t => t.type === "DEBIT").length}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminWallets;